import type { CameraHealthUpdate, OccupancySnapshot, ServerMessage } from '@playplate/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { realtime, type RealtimeStatus } from '../lib/realtime';

/** Subscribe to every realtime message. The callback is kept in a ref so a
 *  re-render does not tear the subscription down and rebuild it. */
export function useRealtimeMessages(onMessage: (message: ServerMessage) => void): void {
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => realtime.subscribe((message) => handler.current(message)), []);
}

export function useRealtimeStatus(): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  useEffect(() => realtime.onStatus((next) => setStatus(next)), []);
  return status;
}

/**
 * Wire the socket into the query cache.
 *
 * Mounted once, at the shell. Live messages invalidate the queries they affect,
 * and a reconnect invalidates everything — the socket may have missed events
 * while it was down, and refetching is how those arrive rather than being
 * silently absent. This is the mechanism that makes a dropped connection cost
 * latency instead of correctness.
 */
export function useRealtimeSync(): { status: RealtimeStatus; occupancy: OccupancySnapshot | null } {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const [occupancy, setOccupancy] = useState<OccupancySnapshot | null>(null);
  const wasDisconnected = useRef(false);

  useEffect(
    () =>
      realtime.onStatus((next) => {
        setStatus(next);
        if (next === 'reconnecting' || next === 'offline') {
          wasDisconnected.current = true;
        } else if (next === 'open' && wasDisconnected.current) {
          wasDisconnected.current = false;
          void queryClient.invalidateQueries();
        }
      }),
    [queryClient],
  );

  useRealtimeMessages((message) => {
    switch (message.type) {
      case 'occupancy':
        // Held in component state rather than the query cache: it arrives once a
        // second and would otherwise churn the cache continuously.
        setOccupancy(message.payload);
        break;

      case 'alert':
      case 'alert.updated':
        void queryClient.invalidateQueries({ queryKey: ['alerts'] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        break;

      case 'event':
        void queryClient.invalidateQueries({ queryKey: ['events'] });
        break;

      case 'camera.health':
        void queryClient.invalidateQueries({ queryKey: ['cameras'] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        break;

      case 'system.health':
        void queryClient.invalidateQueries({ queryKey: ['system'] });
        break;

      default:
        break;
    }
  });

  return { status, occupancy };
}

/** Most recent camera health transitions, for the live ticker on the dashboard. */
export function useCameraHealthFeed(limit = 8): CameraHealthUpdate[] {
  const [updates, setUpdates] = useState<CameraHealthUpdate[]>([]);

  useRealtimeMessages((message) => {
    if (message.type !== 'camera.health') return;
    setUpdates((current) => [message.payload, ...current].slice(0, limit));
  });

  return updates;
}
